import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserCheck, UserMinus, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";

/** Assign a workspace user as remediation owner of a control (optional due date + note). */
export default function AssignOwnerDialog({ tenantId, control, onClose, onSaved }) {
  const owner = control.owner;
  const [users, setUsers] = useState(null);
  const [userId, setUserId] = useState(owner?.user_id || "");
  const [dueAt, setDueAt] = useState(owner?.due_at || "");
  const [note, setNote] = useState(owner?.note || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    authApi.org
      .users()
      .then((list) => {
        const active = list.filter((u) => !u.disabled);
        setUsers(active);
        if (!userId && active.length) setUserId(active[0].id);
      })
      .catch((err) => toast.error("Could not load users", { description: errorMessage(err) }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authApi.compliance.assignOwner(tenantId, control.id, { user_id: userId, due_at: dueAt || null, note });
      toast.success(`${res.name} now owns ${control.id}`, { description: owner?.user_id === res.user_id ? "Assignment updated" : "They have been notified by email and in the bell" });
      onSaved(res);
    } catch (err) {
      toast.error("Could not assign owner", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  const unassign = async () => {
    setBusy(true);
    try {
      await authApi.compliance.clearOwner(tenantId, control.id);
      toast.success("Owner removed");
      onSaved(null);
    } catch (err) {
      toast.error("Could not remove owner", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg tenant-dialog" data-testid="cis-owner-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              <span className="mono">{control.id}</span> · {owner ? "Change owner" : "Assign owner"}
            </DialogTitle>
            <DialogDescription>{control.title}</DialogDescription>
          </DialogHeader>
          <div className="ws-form">
            <label className="auth-field">
              <span>
                <UserCheck size={12} /> Owner (workspace user)
              </span>
              <select className="ws-native-select" value={userId} onChange={(e) => setUserId(e.target.value)} required disabled={!users} data-testid="cis-owner-select">
                {!users && <option value="">Loading...</option>}
                {users?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} · {u.email}
                    {u.role === "admin" ? " (admin)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-field">
              <span>
                <CalendarClock size={12} /> Due date <small className="muted">(optional)</small>
              </span>
              <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="cis-owner-due" />
            </label>
            <label className="auth-field">
              <span>Note for the owner (optional)</span>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Context, ticket number, expected fix..." data-testid="cis-owner-note" />
            </label>
          </div>
          <DialogFooter>
            {owner && (
              <Button type="button" variant="ghost" onClick={unassign} disabled={busy} data-testid="cis-owner-unassign">
                <UserMinus size={14} /> Unassign
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !userId} data-testid="cis-owner-save">
              {busy && <Loader2 size={14} className="spin" />} {owner ? "Save" : "Assign & notify"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
